import { useState, useRef, ChangeEvent, DragEvent, useEffect } from "react";
import styles from "./ImageSelect.module.scss";
import delete_icon from "../../../assets/images/actions/delete.svg";
import { useTranslation } from "react-i18next";

interface ProductImage {
  id: string;
  file: File | null;
  image: string;
  order: number;
  name: string;
  isExisting?: boolean;
}

interface ImageSelectProps {
  onImagesChange: (images: ProductImage[]) => void;
  onDeletedImagesChange?: (deletedIds: string) => void;
  onImageOrderChange?: (orders: { id: number; order: number }[]) => void;
  existingImages?: { id: string; image: string; order: number }[];
  resetKey?: number;
  disableUpload?: boolean;
}

const ImageSelect: React.FC<ImageSelectProps> = ({
  onImagesChange,
  onDeletedImagesChange,
  onImageOrderChange,
  existingImages = [],
  resetKey,
  disableUpload,
}) => {
  const { t } = useTranslation("translation");
  const [images, setImages] = useState<ProductImage[]>([]);
  const [deletedImageIds, setDeletedImageIds] = useState<number[]>([]);
  const [draggedImage, setDraggedImage] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setImages([]);
    setDeletedImageIds([]);
    onImagesChange([]);
  }, [resetKey]);

  useEffect(() => {
    if (existingImages && existingImages.length > 0) {
      const existingProductImages: ProductImage[] = existingImages.map(
        (img) => ({
          id: img.id,
          file: null,
          image: img.image,
          order: img.order,
          name: `Image ${img.order}`,
          isExisting: true,
        }),
      );

      setImages(existingProductImages);
      onImagesChange?.(existingProductImages);
    }
  }, [existingImages, resetKey]);

  const handleImageSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    processFiles(Array.from(files));

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const processFiles = (files: File[]) => {
    const newImages: ProductImage[] = [];

    files.forEach((file) => {
      if (file.type.startsWith("image/")) {
        const id = Math.random().toString(36).substr(2, 9);
        const image = URL.createObjectURL(file);
        const order = images.length + newImages.length + 1;

        newImages.push({
          id,
          file,
          image,
          order,
          name: file.name,
          isExisting: false,
        });
      }
    });

    const updatedImages = [...images, ...newImages];
    setImages(updatedImages);
    onImagesChange?.(updatedImages);
    updateImageOrders(updatedImages);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    if (disableUpload) return;
    e.preventDefault();
    setIsDragOver(true);
  };
  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    if (disableUpload) return;
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
  };

  const handleImageDelete = (id: string) => {
    const imageToDelete = images.find((img) => img.id === id);
    if (imageToDelete?.isExisting) {
      const numericId = Number(id);
      if (!isNaN(numericId)) {
        const newDeletedIds = [...deletedImageIds, numericId];
        setDeletedImageIds(newDeletedIds);
        onDeletedImagesChange?.(newDeletedIds.join(","));
      }
    }

    const updatedImages = images
      .filter((img) => img.id !== id)
      .map((img, index) => ({
        ...img,
        order: index + 1,
      }));

    setImages(updatedImages);
    onImagesChange(updatedImages.length > 0 ? updatedImages : []);
    updateImageOrders(updatedImages);
  };

  const handleDragStart = (e: DragEvent<HTMLDivElement>, id: string) => {
    setDraggedImage(id);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOverItem = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDropItem = (e: DragEvent<HTMLDivElement>, targetId: string) => {
    e.preventDefault();

    if (!draggedImage || draggedImage === targetId) return;

    const draggedIndex = images.findIndex((img) => img.id === draggedImage);
    const targetIndex = images.findIndex((img) => img.id === targetId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    const newImages = [...images];
    const [draggedItem] = newImages.splice(draggedIndex, 1);
    newImages.splice(targetIndex, 0, draggedItem);

    const reorderedImages = newImages.map((img, idx) => ({
      ...img,
      order: idx + 1,
    }));

    setImages(reorderedImages);
    setDraggedImage(null);
    onImagesChange?.(reorderedImages);

    updateImageOrders(reorderedImages);
  };

  const updateImageOrders = (imageList: ProductImage[]) => {
    const orderData = imageList
      .filter((img) => img.isExisting)
      .map((img) => ({
        id: Number(img.id),
        order: img.order,
      }));

    onImageOrderChange?.(orderData);
  };

  const handleDragEnd = () => {
    setDraggedImage(null);
  };

  const handleSelectImages = () => {
    if (disableUpload) return;
    fileInputRef.current?.click();
  };

  return (
    <div className={styles.image_select}>
      {!disableUpload && (
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleImageSelect}
          multiple
          accept=".jpg, .jpeg, .png, .webp"
          className={styles.file_input}
        />
      )}

      <div
        className={`${styles.upload_area} ${
          isDragOver ? styles.drag_over : ""
        }`}
        onClick={handleSelectImages}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {!disableUpload && (
          <>
            <div className={styles.browse_images}>
              {t("buttons.browse_files")}
            </div>
            <p className={styles.upload_hint}>
              {t("products_details.upload_images")}
            </p>
          </>
        )}
        {disableUpload && existingImages?.length === 0 && (
          <p className={styles.upload_hint}>
            {t("products_details.no_images")} <br />{" "}
            {t("products_details.no_images_text")}
          </p>
        )}
      </div>

      {images.length > 0 && (
        <div className={styles.selected_images}>
          <h4 className={styles.images_title}>
            {t("products_details.selected_images")} ({images.length})
            {deletedImageIds.length > 0 && (
              <span className={styles.deleted_info}>
                • {deletedImageIds.length}{" "}
                {t("products_details.deleting_images")}
              </span>
            )}
          </h4>
          <div className={styles.images_grid}>
            {images.map((image) => (
              <div
                key={image.id}
                className={`${styles.image_item} ${
                  draggedImage === image.id ? styles.dragging : ""
                } ${image.isExisting ? styles.existing_image : ""}`}
                draggable
                onDragStart={(e) => handleDragStart(e, image.id)}
                onDragOver={handleDragOverItem}
                onDrop={(e) => handleDropItem(e, image.id)}
                onDragEnd={handleDragEnd}
              >
                <div className={styles.image_container}>
                  <img
                    src={image.image}
                    alt={image.name}
                    className={styles.image}
                  />
                  <div className={styles.image_order}>#{image.order}</div>
                  {image.isExisting && (
                    <div className={styles.existing_badge}>
                      {t("products_details.existing_images")}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleImageDelete(image.id);
                    }}
                    className={styles.delete_btn}
                  >
                    <img src={delete_icon} alt="delete" />
                  </button>
                </div>
                <p className={styles.image_name}>{image.name}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageSelect;
